import { useEffect, type JSX } from 'react';
import { AppShell } from './AppShell/index.js';
import { SseProvider } from './sse/SseProvider.js';
import { OnboardingGate } from './components/onboarding/OnboardingGate.js';
import { AuthGate } from './components/auth/AuthGate.js';
import { TutorialGate } from './components/tutorial/TutorialGate.js';
import { createOnboardingStore } from './state/onboarding.js';
import { useRootStore } from './state/store.js';
import { getHealth } from './api/clients/capabilities.js';
import { getDefaultApiContext } from './api/client.js';
import { getDefaultSettingsStore } from './state/settings.js';

// Demo-recording escape hatch — expose the Zustand root store on window
// when Vite is in dev mode so the playback recording spec can seed
// realistic session metadata (provider name, token count, capabilities).
// No-op in production builds because `import.meta.env.DEV === false`.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __TESSA_ROOT_STORE__?: typeof useRootStore })
    .__TESSA_ROOT_STORE__ = useRootStore;
}

/**
 * Module-level singleton — the onboarding gate's `<OnboardingTakeover>` reads
 * the `completed` / `dismissedAt` flags out of localStorage at slice creation
 * time, so a single shared store keeps the wizard's gating consistent across
 * StrictMode-double-mounts and the AppShell's resize re-renders.
 *
 * Matches the `ContextPanel.tsx` `createSessionsStore()` pattern (W2-S8) for
 * slices that have not yet been promoted into the root store. Composing the
 * onboarding slice into `useRootStore` later requires reconciling the
 * `OnboardingSliceConfig.onComplete` callback (which today captures workspace
 * + provider + apiKey settings flush on `finish()` / `skip()`) with the W6-S2
 * settings panel — out of W2 fix-pass scope.
 */
const onboardingStore = createOnboardingStore();

/**
 * SPA root. Composes:
 *   - `<SseProvider>` so chat SSE auto-connects when an activeSessionId is set
 *     (without it the chat slice never sees `chat:token` / `kb:*` /
 *     `validation:*` / `files:changed` events).
 *   - `<OnboardingGate>` so first-launch users see the takeover wizard before
 *     the AppShell becomes interactive. The gate self-skips when
 *     `tessa.onboarding.completed === '1'` is persisted in localStorage;
 *     subsequent loads render `<AppShell>` unmodified. (W2 fix-pass §7.1.)
 *   - `<AppShell>` — the four-region shell + StatusBar.
 */
export default function App(): JSX.Element {
  // Pre-fill the onboarding workspace path from the server-known workspace
  // root when the slice is still empty. Without this seed the wizard's
  // "Workspace path" input opens blank even though the server is already
  // serving a configured workspace (visible in the bottom status bar).
  useEffect(() => {
    const slice = onboardingStore.getState();
    if (slice.workspacePath.trim().length > 0) return;
    void (async () => {
      const res = await getHealth(getDefaultApiContext());
      if (!res.ok) return;
      const root = res.value.workspaceRoot;
      if (typeof root === 'string' && root.length > 0) {
        // Re-check inside the async callback in case the user typed something
        // in the meantime.
        if (onboardingStore.getState().workspacePath.trim().length === 0) {
          onboardingStore.getState().setWorkspacePath(root);
        }
      }
    })();
  }, []);

  // Load the merged-settings snapshot once so cross-panel consumers (the
  // chat composer's `llm.contextWindows` lookup, etc.) have data on first
  // render. Idempotent — the slice short-circuits when status !== 'idle'.
  useEffect(() => {
    const store = getDefaultSettingsStore();
    if (store.getState().status === 'idle') {
      void store.getState().loadMergedSettings();
    }
  }, []);

  return (
    <AuthGate>
      <SseProvider>
        <OnboardingGate store={onboardingStore}>
          <AppShell />
          <TutorialGate onboardingStore={onboardingStore} />
        </OnboardingGate>
      </SseProvider>
    </AuthGate>
  );
}
